import React, { useState } from 'react';
import { Lock, User, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { ApiError } from '../types/api';

interface LoginProps {
  onLogin: (success: boolean) => void;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      await login(formData.email, formData.password);
      onLogin(true);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        console.error('Login error:', err);
        if (err instanceof TypeError && err.message.includes('fetch')) {
          setError('No se puede conectar al servidor. Verifica que el servidor esté ejecutándose en https://centdos-backend-production.up.railway.app');
        } else {
          setError(`Error de conexión: ${err instanceof Error ? err.message : 'Unknown error'}`);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-secondary-100 p-4">
      <div className="max-w-md w-full space-y-6 lg:space-y-8">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 lg:h-16 lg:w-16 mb-3 lg:mb-4">
            <img src="/logo.png" alt="CENTAdmin Logo" className="h-12 w-12 lg:h-16 lg:w-16 rounded-full" />
          </div>
          <h2 className="text-2xl lg:text-3xl font-bold text-secondary-900 mb-2">
            CENTAdmin Dashboard
          </h2>
          <p className="text-sm lg:text-base text-secondary-600">
            Inicia sesión en tu sistema de administración
          </p>
        </div>

        <div className="card p-6 lg:p-8">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-secondary-700 mb-2">
                Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-secondary-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="text"
                  required
                  className="input-field pl-10"
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-secondary-700 mb-2">
                Contraseña
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-secondary-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  className="input-field pl-10 pr-10"
                  placeholder="Contraseña"
                  value={formData.password}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-secondary-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-secondary-400" />
                  )}
                </button>
              </div>
            </div>



            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full flex justify-center items-center"
            >
              {isLoading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                'Iniciar Sesión'
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-secondary-600">
              Ingresa tus credenciales de administrador válidas
            </p>
            <p className="text-xs text-secondary-500 mt-2">
              Si no tienes credenciales, contacta al administrador del sistema
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
